// pages/auth/SignInPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from "../ui/button"
import { Card, CardContent, CardFooter } from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Checkbox } from "../ui/checkbox"
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

interface FormData {
  email: string;
  password: string;
  remember: boolean;
}

export default function SignInPage(): React.ReactElement {
  const { signin, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    password: '',
    remember: false
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>('');

  // Get the intended destination from location state
  const from = (location.state as any)?.from?.pathname || null;

  // Clear any previous errors when component mounts
  useEffect(() => {
    setFormError('');
  }, []);

  // Redirect authenticated users immediately
  useEffect(() => {
    if (!authLoading && user) {
      // Check if user is trying to access a route they're not allowed to
      if (from) {
        // Admin can only access /admin routes
        if (user.role === 'admin' && from.startsWith('/admin')) {
          navigate(from, { replace: true });
          return;
        }
        // Users can only access /dashboard routes
        else if (user.role === 'user' && from.startsWith('/dashboard')) {
          navigate(from, { replace: true });
          return;
        }
        // Formateurs can access /formateur routes
        else if (user.role === 'formateur' && from.startsWith('/formateur')) {
          navigate(from, { replace: true });
          return;
        }
      }

      // Default redirection based on role
      const defaultPath = user.role === 'admin' ? '/admin' : 
                         user.role === 'formateur' ? '/formateur' : '/dashboard';
      navigate(defaultPath, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear errors when user starts typing
    if (formError) setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    try {
      const result = await signin({
        email: formData.email,
        password: formData.password
      });

      if (result.success && result.user) {
        // Check intended destination
        if (from) {
          // Admin can only access /admin routes
          if (result.user.role === 'admin' && from.startsWith('/admin')) {
            navigate(from, { replace: true });
            return;
          }
          // Users can only access /dashboard routes
          else if (result.user.role === 'user' && from.startsWith('/dashboard')) {
            navigate(from, { replace: true });
            return;
          }
          // Formateurs can access /formateur routes
          else if (result.user.role === 'formateur' && from.startsWith('/formateur')) {
            navigate(from, { replace: true });
            return;
          }
        }

        // Default redirection based on role
        const defaultPath = result.user.role === 'admin' ? '/admin' : 
                           result.user.role === 'formateur' ? '/formateur' : '/dashboard';
        navigate(defaultPath, { replace: true });
      } else {
        const errorMessage = result.error || 'Échec de la connexion';
        setFormError(errorMessage);
      }
    } catch (error: any) {
      let errorMessage = 'Une erreur est survenue lors de la connexion';

      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || error.response.data.error || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setFormError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAuth = (): void => {
    // Store the intended destination before redirecting to Google
    if (from) {
      sessionStorage.setItem('authRedirect', from);
    }
    window.location.href = authAPI.getGoogleAuthUrl();
  };

  const handleFacebookAuth = (): void => {
    // Store the intended destination before redirecting to Facebook
    if (from) {
      sessionStorage.setItem('authRedirect', from);
    }
    window.location.href = authAPI.getFacebookAuthUrl();
  };

  const handleLinkedInAuth = (): void => {
    // Store the intended destination before redirecting to LinkedIn
    if (from) {
      sessionStorage.setItem('authRedirect', from);
    }
    window.location.href = authAPI.getLinkedInAuthUrl();
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setFormError('Veuillez entrer votre email d\'abord');
      return;
    }

    try {
      await authAPI.sendVerificationEmail(formData.email);
      setFormError('');
      alert('Email de vérification envoyé !');
    } catch (error: any) {
      console.error('Error resending verification:', error);
      const errorMsg = error.response?.data?.message || 'Erreur lors de l\'envoi de l\'email';
      setFormError(errorMsg);
    }
  };

  // Show loading while checking initial auth status
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-accent">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00088E] mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-accent py-12 px-4 sm:px-6 lg:px-8 dark:bg-gray-900">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center justify-center text-center">
          <h1 className="mt-6 text-3xl font-serif font-bold tracking-tight">Se connecter</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Accédez à votre compte pour suivre vos formations et ressources
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="votre.email@exemple.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Mot de passe</Label>
                  <a href="/forgot-password" className="text-sm font-medium text-elille-blue hover:underline">
                    Mot de passe oublié?
                  </a>
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  name="remember"
                  checked={formData.remember}
                  onCheckedChange={(checked: boolean | string) =>
                    setFormData(prev => ({ ...prev, remember: Boolean(checked) }))
                  }
                  disabled={isSubmitting}
                />
                <Label
                  htmlFor="remember"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Se souvenir de moi
                </Label>
              </div>

              {formError && (
                <div className={`p-3 text-sm border rounded-md ${
                  formError.includes('contacter le support') || formError.includes('vérifier votre email')
                    ? 'text-orange-700 bg-orange-50 border-orange-200 dark:bg-orange-900/10 dark:text-orange-400 dark:border-orange-800'
                    : 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/10 dark:text-red-400 dark:border-red-800'
                }`}>
                  <div className="flex items-start space-x-2">
                    {formError.includes('contacter le support') && (
                      <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    )}
                    {formError.includes('vérifier votre email') && (
                      <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                    )}
                    {!formError.includes('contacter le support') && !formError.includes('vérifier votre email') && (
                      <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                    <div className="flex-1">
                      <p className="font-medium">
                        {formError.includes('contacter le support') && 'Accès refusé'}
                        {formError.includes('vérifier votre email') && 'Vérification requise'}
                        {!formError.includes('contacter le support') && !formError.includes('vérifier votre email') && 'Erreur de connexion'}
                      </p>
                      <p className="mt-1">{formError}</p>

                      {formError.includes('contacter le support') && (
                        <div className="mt-3">
                          <a
                            href="/contact"
                            className="text-sm font-medium text-orange-700 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 underline"
                          >
                            Contacter le support →
                          </a>
                        </div>
                      )}

                      {formError.includes('vérifier votre email') && (
                        <div className="mt-3 space-y-2">
                          <p className="text-sm">
                            Vous n'avez pas reçu l'email de vérification ?
                          </p>
                          <button
                            type="button"
                            onClick={handleResendVerification}
                            className="text-sm font-medium text-orange-700 hover:text-orange-800 dark:text-orange-400 dark:hover:text-orange-300 underline"
                          >
                            Renvoyer l'email de vérification
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-elille-blue bg-[#00088E] hover:bg-elille-blue/90"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Ou continuer avec</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-3">
              {/* Google Auth Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogleAuth}
                type="button"
                disabled={isSubmitting}
              >
                <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50">
                  <path d="M 26 2 C 13.308594 2 3 12.308594 3 25 C 3 37.691406 13.308594 48 26 48 C 35.917969 48 41.972656 43.4375 45.125 37.78125 C 48.277344 32.125 48.675781 25.480469 47.71875 20.9375 L 47.53125 20.15625 L 46.75 20.15625 L 26 20.125 L 25 20.125 L 25 30.53125 L 36.4375 30.53125 C 34.710938 34.53125 31.195313 37.28125 26 37.28125 C 19.210938 37.28125 13.71875 31.789063 13.71875 25 C 13.71875 18.210938 19.210938 12.71875 26 12.71875 C 29.050781 12.71875 31.820313 13.847656 33.96875 15.6875 L 34.6875 16.28125 L 41.53125 9.4375 L 42.25 8.6875 L 41.5 8 C 37.414063 4.277344 31.960938 2 26 2 Z M 26 4 C 31.074219 4 35.652344 5.855469 39.28125 8.84375 L 34.46875 13.65625 C 32.089844 11.878906 29.199219 10.71875 26 10.71875 C 18.128906 10.71875 11.71875 17.128906 11.71875 25 C 11.71875 32.871094 18.128906 39.28125 26 39.28125 C 32.550781 39.28125 37.261719 35.265625 38.9375 29.8125 L 39.34375 28.53125 L 27 28.53125 L 27 22.125 L 45.84375 22.15625 C 46.507813 26.191406 46.066406 31.984375 43.375 36.8125 C 40.515625 41.9375 35.320313 46 26 46 C 14.386719 46 5 36.609375 5 25 C 5 13.390625 14.386719 4 26 4 Z"></path>
                </svg>
                Google
              </Button>

              {/* Facebook Auth Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={handleFacebookAuth}
                type="button"
                disabled={isSubmitting}
              >
                <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </Button>

              {/* LinkedIn Auth Button */}
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLinkedInAuth}
                type="button"
                disabled={isSubmitting}
              >
                <svg className="mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </Button>
            </div>

            <p className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
              Vous n'avez pas de compte?{" "}
              <a href="/signup" className="font-medium text-elille-blue hover:underline">
                S'inscrire
              </a>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
